import {
    autoUpdate,
    FloatingFocusManager,
    FloatingPortal,
    offset,
    shift,
    size,
    useDismiss,
    useFloating,
    useFocus,
    useInteractions,
    useRole
} from '@floating-ui/react';
import { FormInput } from '@form/index';
import { cx } from 'class-variance-authority';
import { head } from 'lodash';
import { ComponentProps, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useFormContext } from 'react-hook-form';
import { Column, DataTable, Selection } from '../../DataTable';

interface Props<T = unknown> extends Omit<
    ComponentProps<typeof DataTable>,
    'fetchUrl' | 'columns' | 'ListEmptyComponent'
> {
    name: string;
    label?: string;
    fetchUrl: string;
    tableColumns: Column<T>[];
    keyResolver?: (item: T) => string;
    inputProps?: Partial<ComponentProps<typeof FormInput>>;
    onSelect?: (item: T) => any;
    className?: string;
    enabled?: boolean;
    ListEmptyComponent?: (close: () => void) => React.ReactNode;
}

const KIOSK_KEYBOARD_ID = 'KioskBoard-VirtualKeyboard';

// true when a close was caused by the kiosk on-screen keyboard (its per-keystroke input.focus() churn or a tap on a key)
const isKioskKeyboardInteraction = (event?: Event, reason?: string) => {
    const keyboard = document.getElementById(KIOSK_KEYBOARD_ID);
    if (!keyboard) return false;
    if (reason === 'focus' || reason === 'focus-out') return true;
    const target = event?.target;
    return target instanceof Node && keyboard.contains(target);
};

const AsyncFormInput = ({
    name,
    label,
    fetchUrl,
    tableColumns,
    keyResolver,
    inputProps,
    onSelect,
    className,
    enabled,
    ...props
}: Props) => {
    const { watch, setValue, setFocus } = useFormContext();
    const value = watch(name);
    const [maxHeight, setMaxHeight] = useState(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        // keep the panel mounted through the blur/refocus churn the kiosk on-screen keyboard causes on every key tap
        onOpenChange: (open, event, reason) => {
            if (!open && isKioskKeyboardInteraction(event, reason)) return;
            if (open) setFocus(name);
            setIsOpen(open);
        },
        middleware: [
            offset(4),
            shift(),
            size({
                apply({ availableHeight }) {
                    flushSync(() => setMaxHeight(availableHeight));
                }
            })
        ],
        whileElementsMounted: autoUpdate,
        placement: 'bottom'
    });

    const focus = useFocus(context, { enabled });
    const dismiss = useDismiss(context);
    const role = useRole(context);

    const ListEmptyComponent = useMemo(
        () => props.ListEmptyComponent?.(() => setIsOpen(false)),
        [props.ListEmptyComponent, setIsOpen]
    );

    const { getReferenceProps, getFloatingProps } = useInteractions([focus, dismiss, role]);

    return (
        <div
            className={cx(className, 'tw-relative')}
            ref={(ref) => {
                refs.setReference(ref);
                containerRef.current = ref;
            }}
            {...getReferenceProps()}
        >
            {isOpen && (
                <FloatingPortal>
                    <FloatingFocusManager disabled context={context} modal={false}>
                        <div
                            ref={refs.setFloating}
                            style={{
                                ...floatingStyles,
                                maxWidth: containerRef.current?.clientWidth,
                                maxHeight: maxHeight - 12
                            }}
                            className="tw-z-[999999] tw-flex tw-h-[50vh] tw-flex-col tw-rounded-xl tw-bg-white tw-shadow-2xl"
                            {...getFloatingProps()}
                        >
                            <DataTable
                                hideSelectionAssistant
                                fullTextValue={value}
                                hideSearchBar
                                keyResolver={keyResolver}
                                hideSelectAll
                                tableProps={{
                                    onSelectionChange: (keys, items) => {
                                        if (onSelect) onSelect(head(items));
                                        else setValue(name, head(Array.from(keys) as Selection));

                                        containerRef.current?.blur();
                                        setIsOpen(false);
                                    },
                                    selectedKeys: [value],
                                    defaultSelectedKeys: [value],
                                    selectionMode: 'single',
                                    compact: true
                                }}
                                fetchUrl={fetchUrl}
                                columns={tableColumns}
                                {...props}
                                ListEmptyComponent={ListEmptyComponent}
                            />
                        </div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
            <FormInput {...inputProps} name={name} label={label} />
        </div>
    );
};

export default AsyncFormInput;
